import { getAquilaApiClient } from "../api/aquilaApi/aquilaApi";
import {
    getBooleanFromString,
    getValueFromSearchParams,
    getWindowValue,
    WindowProperty,
} from "../utils/window.utils";

import { authStore } from "./auth";
import type { UserPreference } from "./userPreferences";
import { UserPreferenceData, UserPreferencesStore } from "./userPreferences";
import { zustandStore } from "./zustand.helpers";

export enum FeatureFlags {
    UAT_PACKAGED_INSIGHT = "uatPackagedInsights",
    UAT_PEER_BENCHMARKING = "uatPeerBenchmarking",
    UAT_STANDALONE = "uatStandalone",
    SUPPORT_MODE = "supportMode",
    UNIFIED_SEARCH = "unifiedSearch",
    ALL_FEATURE_FLAGS_DROP_DOWN = "allFeatureFlagsDropDown",
    FORGE_VIZ = "forgeViz",
    THUMBS_FEEDBACK_ON_CARD = "thumbsFeedbackCard",
    GLOBAL_WORKBENCH = "globalWorkbench",
    ENABLE_TEST_ACCOUNTS = "enableTestAccounts",
    UX_TREATMENTS = "uxTreatmentsOnSidePanel",
    ADD_ADVERTISER_DROPDOWN_OPTION = "addAdvertiserDropdownOption",
    CLOUDWATCH_RUM = "cloudwatchRUM",
    USER_PREFERENCE_PANEL = "userPreferencePanel",
    PITCH_WRITE_BOTH_READ_ACC = "pitchWriteBothReadACC",
    PITCH_WRITE_BOTH_READ_PLM = "pitchWriteBothReadPLM",
    PITCH_WRITE_AND_READ_PLM = "pitchWriteAndReadPLM",
    MARKETING_CONTENT_TYPE_CONSOLIDATION = "marketingContentTypeConsolidation",
    FORGE_OPTIN_TOGGLE = "forgeOptinToggle",
}

export type FeatureFlagsState = {
    [flag in FeatureFlags]: boolean;
};

export const initialState: FeatureFlagsState = {
    [FeatureFlags.UNIFIED_SEARCH]: true,
    [FeatureFlags.FORGE_VIZ]: false,
    [FeatureFlags.THUMBS_FEEDBACK_ON_CARD]: false,
    [FeatureFlags.GLOBAL_WORKBENCH]: false,
    [FeatureFlags.UX_TREATMENTS]: false,
    [FeatureFlags.USER_PREFERENCE_PANEL]: false,
    [FeatureFlags.ADD_ADVERTISER_DROPDOWN_OPTION]: false,
    [FeatureFlags.CLOUDWATCH_RUM]: false,
    [FeatureFlags.PITCH_WRITE_BOTH_READ_ACC]: false,
    [FeatureFlags.PITCH_WRITE_BOTH_READ_PLM]: false,
    [FeatureFlags.PITCH_WRITE_AND_READ_PLM]: false,
    [FeatureFlags.MARKETING_CONTENT_TYPE_CONSOLIDATION]: false,
    [FeatureFlags.FORGE_OPTIN_TOGGLE]: false,

    // Always here
    [FeatureFlags.UAT_PACKAGED_INSIGHT]: false,
    [FeatureFlags.UAT_PEER_BENCHMARKING]: false,
    [FeatureFlags.UAT_STANDALONE]: false,
    [FeatureFlags.SUPPORT_MODE]: false,
    [FeatureFlags.ALL_FEATURE_FLAGS_DROP_DOWN]: false,
    [FeatureFlags.ENABLE_TEST_ACCOUNTS]: false,
};

export const featureFlagLabelMap: {
    [flag in FeatureFlags]: string;
} = {
    [FeatureFlags.UNIFIED_SEARCH]: "Unified Search",
    [FeatureFlags.FORGE_VIZ]: "Forge Visualization",
    [FeatureFlags.THUMBS_FEEDBACK_ON_CARD]: "Thumbs Feedback on Card",
    [FeatureFlags.GLOBAL_WORKBENCH]: "Global Workbench",
    [FeatureFlags.ADD_ADVERTISER_DROPDOWN_OPTION]:
        "Add Advertiser Dropdown Option",
    [FeatureFlags.CLOUDWATCH_RUM]: "CloudWatch RUM",
    [FeatureFlags.PITCH_WRITE_BOTH_READ_ACC]:
        "PLM Migration: Write to Both Services, Read from ACC",
    [FeatureFlags.PITCH_WRITE_BOTH_READ_PLM]:
        "PLM Migration: Write to Both Services, Read from PLM",
    [FeatureFlags.PITCH_WRITE_AND_READ_PLM]:
        "PLM Migration: Write and Read from PLM Only",

    // Always here
    [FeatureFlags.UAT_PACKAGED_INSIGHT]: "UAT Packaged Insight",
    [FeatureFlags.UAT_PEER_BENCHMARKING]: "UAT Peer Benchmarking",
    [FeatureFlags.UAT_STANDALONE]: "UAT Standalone Families",
    [FeatureFlags.SUPPORT_MODE]: "Support Mode",
    [FeatureFlags.ALL_FEATURE_FLAGS_DROP_DOWN]: "All Feature Flags Drop Down",
    [FeatureFlags.ENABLE_TEST_ACCOUNTS]: "ENABLE_TEST_ACCOUNTS",
    [FeatureFlags.UX_TREATMENTS]: "UX Treatments on SidePanel",
    [FeatureFlags.USER_PREFERENCE_PANEL]: "User Preference Panel",
    [FeatureFlags.MARKETING_CONTENT_TYPE_CONSOLIDATION]:
        "Marketing Content Type Consolidation",
    [FeatureFlags.FORGE_OPTIN_TOGGLE]: "Forge Opt-in Toggle",
};

export const READY_TO_UAT_FEATURES: FeatureFlags[] = [
    FeatureFlags.UAT_PACKAGED_INSIGHT,
    FeatureFlags.UAT_STANDALONE,
];

export interface FeatureFlagsActions {
    setFeatureFlags: (flags: Partial<FeatureFlagsState>) => void;
    resetFeatureFlags: () => void;
}

export const { store: FeatureFlagsStore } = zustandStore<
    FeatureFlagsState,
    FeatureFlagsActions
>("FeatureFlags", initialState, (set: any) => ({
    setFeatureFlags: (flags: Partial<FeatureFlagsState>) =>
        set(() => ({ ...flags })),
    resetFeatureFlags: () => set(() => ({ ...initialState })),
}));

const getAquilaFlags = async (
    requesterAlias: string,
): Promise<{ [key: string]: boolean }> => {
    try {
        const {
            settings: { aquilaConfig },
        } = authStore.getState();
        const aquilaFlags = await (
            await getAquilaApiClient()
        ).getAccessByAppConfig({
            requesterAlias,
            ...aquilaConfig.featureAccess,
        });

        return aquilaFlags.data
            .filter((f) => f.allowed)
            .reduce<{ [key: string]: boolean }>((obj, flag) => {
                return { ...obj, [flag.name!]: true };
            }, {});
    } catch (e: any) {
        return {};
    }
};

const isFeatureEnabledWithPrecedence = (
    flag: string,
    aquilaFlags: { [key: string]: boolean },
): boolean => {
    // First precedence set for value set from URL param
    const urlParamValue = getValueFromSearchParams(flag);
    if (urlParamValue !== undefined) {
        return getBooleanFromString(urlParamValue);
    }

    // Second precedence set from user preferences
    const userPreferenceKey = (
        Object.keys(UserPreferenceData) as UserPreference[]
    ).find((key) => UserPreferenceData[key].featureFlagControl === flag);
    if (userPreferenceKey) {
        // Explicitely pull from the state instead of getPreference() so we avoid an infinite loop
        const preference =
            UserPreferencesStore.getState().preferences[userPreferenceKey];
        if (preference !== undefined) {
            // Returns false for non-boolean values
            return getBooleanFromString(preference);
        }
    }

    // Third precedence set for value set from aquila
    if (flag in aquilaFlags) {
        return aquilaFlags[flag];
    }

    // Fourth precedence set from weblab
    const weblabValue = getWindowValue(
        `${WindowProperty.IHUB_STATE}.weblabs.${flag}`,
    );
    if (weblabValue !== undefined) {
        return !!weblabValue;
    }

    // Fifth precedence set from initial state
    return initialState[flag] ?? false;
};

export const getFlagValues = async (
    requesterAlias: string,
): Promise<FeatureFlagsState> => {
    const aquilaFlags = await getAquilaFlags(requesterAlias);
    return Object.keys(initialState).reduce((obj, flag) => {
        if (flag in obj) {
            return {
                ...obj,
                [flag]: isFeatureEnabledWithPrecedence(flag, aquilaFlags),
            };
        }
        return {
            ...obj,
        };
    }, initialState);
};
